/**
 * BirthDateFields - a date of birth as three lists: day, month, year
 * -------------------------------------------------------------------
 * Added 17 September 2026, when students stopped typing their age.
 *
 * WHY NOT THE BROWSER'S OWN DATE BOX. Two reasons, both already paid for:
 *
 *   - HANDOFF section 14: Chrome draws `<input type="date">` in ITS OWN
 *     locale and ignores the page, so on a machine set to US English it puts
 *     the month first. "03/04/2009" is a different birthday depending on who
 *     reads it, and the person reading is deciding whether a child needs a
 *     parent's permission.
 *   - A calendar is the wrong tool for a date sixteen years ago. On a phone it
 *     opens on today and asks for two hundred taps backwards, and people give
 *     up and pick something nearby - which is a wrong age.
 *
 * Three lists are what a phone does best: each opens the phone's own picker,
 * and the order is day, month, year, the way it is written in Italy.
 *
 * The value going in and out is "2009-03-17", or '' until all three are
 * chosen AND make a real date. 31 February is not quietly turned into
 * 3 March; the box says it does not exist.
 */

import { useEffect, useState } from 'react';

import { useI18n } from '../hooks/useI18n';
import { monthNames } from '../utils/format';

const pad = (n) => String(n).padStart(2, '0');

function split(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return { day: '', month: '', year: '' };
  return { year: match[1], month: String(Number(match[2])), day: String(Number(match[3])) };
}

/** "2009-03-17" from the three parts, or '' if they are incomplete or not a real day. */
function join({ day, month, year }) {
  if (!day || !month || !year) return '';
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  const date = new Date(Date.UTC(y, m - 1, d));
  const real = date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
  return real ? `${y}-${pad(m)}-${pad(d)}` : '';
}

/**
 * Which years to offer, newest first.
 *
 * Wider than the 16-21 the server accepts, on purpose. A fifteen-year-old who
 * cannot find their own year in the list learns nothing; one who can pick it
 * is told plainly why they cannot join yet.
 */
function yearOptions() {
  const now = new Date().getFullYear();
  return Array.from({ length: 19 }, (_, i) => now - 12 - i);
}

export default function BirthDateFields({
  id = 'dateOfBirth',
  value,
  onChange,
  legend,
  hint,
  error,
  disabled = false,
  // Off on Settings, where an older account may add its date once but must
  // still be able to save everything else without it.
  required = true,
}) {
  const { t } = useI18n();
  const [parts, setParts] = useState(() => split(value));

  // A value changed from outside - a restored draft - is shown. One this
  // component produced itself is not re-split, or picking a day would be
  // undone while the month was still empty.
  useEffect(() => {
    if (value && value !== join(parts)) setParts(split(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const update = (patch) => {
    const next = { ...parts, ...patch };
    setParts(next);
    onChange(join(next));
  };

  const allChosen = Boolean(parts.day && parts.month && parts.year);
  const impossible = allChosen && !join(parts);
  const months = monthNames();

  return (
    <fieldset className="field birth-date" disabled={disabled}>
      <legend className="label">{legend}</legend>

      <div className="birth-date-row">
        <select
          id={`${id}-day`}
          aria-label={t('birthDate.day')}
          value={parts.day}
          onChange={(event) => update({ day: event.target.value })}
          aria-invalid={Boolean(error) || impossible}
          required={required}
        >
          {/* A short word, because this box is narrow: "Giorno" did not fit it
              at 375px, so Italian shows the "GG" of GG/MM/AAAA. The
              aria-label above still says the whole word. */}
          <option value="">{t('birthDate.dayShort')}</option>
          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
            <option key={d} value={String(d)}>{d}</option>
          ))}
        </select>

        <select
          id={`${id}-month`}
          aria-label={t('birthDate.month')}
          value={parts.month}
          onChange={(event) => update({ month: event.target.value })}
          aria-invalid={Boolean(error) || impossible}
          required={required}
        >
          <option value="">{t('birthDate.month')}</option>
          {months.map((name, i) => (
            <option key={name} value={String(i + 1)}>{name}</option>
          ))}
        </select>

        <select
          id={`${id}-year`}
          aria-label={t('birthDate.year')}
          value={parts.year}
          onChange={(event) => update({ year: event.target.value })}
          aria-invalid={Boolean(error) || impossible}
          required={required}
        >
          <option value="">{t('birthDate.year')}</option>
          {yearOptions().map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>
      </div>

      {hint && <span className="hint">{hint}</span>}
      {impossible && <span className="field-error">{t('birthDate.impossible')}</span>}
      {error && <span className="field-error">{error}</span>}
    </fieldset>
  );
}
